import { createApp } from "vue";
import "amfe-flexible";
import App from "./App.vue";
import router from "./router";
import store from "./store";
import vant from "./common/vantui"; // 移动端适配
import {
  isTokenExpired,
  clearToken,
  redirectToWxAuth,
  shouldRetryAuth,
  markReauthorization,
  rememberAuthReturnRoute,
} from "@/utils/wechat";
import { hideShareMenus } from "@/utils/wecomSdk";

// 微信 H5 被切到后台再返回时，token（如 12 分钟有效期）可能已过期；
// 提前检查并清理，避免用户操作时才收到 401 中断流程
const checkTokenExpiry = () => {
  // 正在授权回跳（URL 携带 code/accessToken）或路由守卫已在执行授权跳转时跳过，
  // 交由路由守卫统一处理换取 token，避免与 beforeEach 的 redirectToWxAuth 竞态产生双条记录
  const params = new URLSearchParams(window.location.search);
  if (
    params.has("code") ||
    params.has("accessToken") ||
    sessionStorage.getItem("wx_auth_redirecting")
  ) {
    return;
  }
  if (isTokenExpired()) {
    // 关键修复：先显式标记再清 token，保证重新授权后能正确回跳；
    // 若直接先清再调 redirectToWxAuth，内部 hadTokenBeforeRedirect 会为 false
    // 导致本次回跳丢失 returnRoute；同时先标记可避免与 router.beforeEach
    // 的 next(true) 竞态产生第二份空页面
    if (localStorage.getItem("token")) {
      markReauthorization();
      rememberAuthReturnRoute();
    }
    clearToken();
    if (shouldRetryAuth()) {
      redirectToWxAuth();
    }
  }
};
window.addEventListener("pageshow", checkTokenExpiry);
document.addEventListener("visibilitychange", () => {
  if (document.visibilityState === "visible") {
    checkTokenExpiry();
    // 从后台切回时企业微信可能恢复右上角默认菜单，重申一次隐藏
    void hideShareMenus();
  }
});

// 兜底关闭微信/企业微信内置浏览器的原生整页下拉刷新/橡皮筋（不识别
// overscroll-behavior 的老内核）。仅在页面与触点所在的滚动容器都已到顶、
// 且手势继续向下时拦截默认行为，正常滚动和 van-pull-refresh 不受影响。
const disableNativePageBounce = () => {
  let startX = 0;
  let startY = 0;

  document.addEventListener(
    "touchstart",
    (event) => {
      startX = event.touches[0].clientX;
      startY = event.touches[0].clientY;
    },
    { passive: true }
  );

  document.addEventListener(
    "touchmove",
    (event) => {
      const touch = event.touches[0];
      // 横向/斜向手势（左滑返回、横滑组件）一律放行，避免打断系统返回手势
      if (
        Math.abs(touch.clientX - startX) >= Math.abs(touch.clientY - startY)
      ) {
        return;
      }
      // 只处理「从顶部继续往下拉」的手势，其余交给浏览器默认滚动
      if (touch.clientY <= startY || window.scrollY > 0) return;

      let node = event.target as HTMLElement | null;
      while (
        node &&
        node !== document.body &&
        node !== document.documentElement
      ) {
        if (node.scrollHeight > node.clientHeight + 1) {
          // 内部滚动容器（弹窗、选择器等）还没滚到顶时不拦截
          if (node.scrollTop > 0) return;
        }
        node = node.parentElement;
      }
      event.preventDefault();
    },
    { passive: false }
  );
};
disableNativePageBounce();

// 企业微信内全局隐藏右上角「转发/分享、复制链接」菜单。
// 路由切换后重申一次（客户端偶发恢复默认菜单），内部有节流，可放心高频触发
router.afterEach(() => {
  void hideShareMenus();
});

const app = createApp(App);
// 全局注册组件
import NavBar from "@/components/navBar/index.vue";
import Improt from "@/components/import/index.vue";
import datePopup from "@/components/datePopup/index.vue";

import { PullRefresh } from "vant";

app.component("NavBar", NavBar);
app.component("Improt", Improt);
app.component("datePopup", datePopup);

// .use(vconsole)
import VConsole from "vconsole";
const vconsole = new VConsole() as any;

import eventBus from "@/utils/eventBus";

const EventBus = {
  install(appStore: any) {
    appStore.provide("eventBus", eventBus);
  },
};

app
  .use(store)
  .use(PullRefresh)
  .use(router)
  .use(vant)
  .use(EventBus)
  .use(vconsole)
  .mount("#app");
